import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
  clearJourneySnapshot,
  loadJourneySnapshot,
  saveJourneySnapshot,
} from '@/features/credito/stores/creditoSessionPersistence';
import type { CustomerProfileForm, CustomerProfileInput } from '@/features/credito/types/customerProfile';
import { emptyCustomerProfileForm } from '@/features/credito/types/customerProfile';
import { birthDateToIso, isoToBirthDate } from '@/helpers/dates';
import { cleanDoc, formatCpf, maskCelular } from '@/helpers/formatters';
import { pixKeyDigitsForApi } from '@/features/credito/validations/customerProfileValidation';
import type { Faixa } from '@/features/credito/domain/ofertaValores';
import type { IntencaoDeclarada, VinculoDeclarado } from '@/features/credito/types/qualificacao';
import { sessionService } from '@/services';
import type CustomerProfilePrefillResource from '@/models/resources/credito/CustomerProfilePrefillResource';
import SimulationOptionsResource from '@/models/resources/credito/SimulationOptionsResource';
import type { SimulationTermOptionResource } from '@/models/resources/credito/SimulationOptionsResource';
import SimulationResource, { SimulationAlternativeResource } from '@/models/resources/credito/SimulationResource';

const DEFAULT_TERM_MONTHS = 24;

/**
 * O que sobrevive ao retorno da assinatura: a oferta e a proposta, nada de dado cadastral.
 * Nenhum campo do formulário (nome, contato, endereço, chave PIX) vai para o sessionStorage — o
 * perfil já está no backend depois do consentimento, e o CPF é sempre relido de
 * `GET /v1/customer-profile`. Assim a régua de PII é a mesma para todos os campos, e não só o CPF.
 */
interface JourneySnapshot {
  consentGiven: boolean;
  lgpdAccepted: boolean;
  payrollAcknowledged: boolean;
  consentId: string | null;
  simulation: SimulationResource | null;
  simulationOptions: SimulationOptionsResource | null;
  selectedTermMonths: number;
  selectedAmount: number | null;
  selectedBand: Faixa | null;
  maximumOfferedByTerm: Record<number, number>;
  withInsurance: boolean;
  proposalId: string | null;
  userHash: string | undefined;
}

/**
 * Estado da jornada de consignado CLT. Fase 1 é sempre proativa (consumidor sem dívida):
 * não existe acordo, comparativo de dívida nem escrow, então `agreementId` nunca é enviado.
 */
export const useCreditoJourneyStore = defineStore('credito-journey', () => {
  const snapshot = loadJourneySnapshot<JourneySnapshot>();
  const currentUserHash = () => sessionService.token?.payload.hash;

  // Snapshot de outra pessoa (troca de conta na mesma aba) não é reaproveitado.
  const usableSnapshot = snapshot?.userHash === currentUserHash() ? snapshot : null;

  // Fora do snapshot de propósito: as perguntas são refeitas a cada tentativa.
  const qualificacaoVinculo = ref<VinculoDeclarado | null>(null);
  const qualificacaoIntencao = ref<IntencaoDeclarada | null>(null);

  const consentGiven = ref(usableSnapshot?.consentGiven ?? false);
  // Autorização LGPD e ciência do desconto são dadas na tela de autorização, antes do formulário.
  const lgpdAccepted = ref(usableSnapshot?.lgpdAccepted ?? false);
  const payrollAcknowledged = ref(usableSnapshot?.payrollAcknowledged ?? false);
  // Id do consentimento mais recente (POST /v1/consents), para propagar nos eventos do data hub que
  // seguem o consentimento. Append-only: a troca de chave PIX grava um segundo registro, e este
  // campo passa a apontar para o novo — ver `markPixConsented`.
  const consentId = ref<string | null>(usableSnapshot?.consentId ?? null);
  // Chave PIX que já foi registrada em consentimento. A pessoa escolhe a conta depois de ver a
  // oferta, então mudar de chave exige um novo consentimento antes de criar a proposta. Fora do
  // snapshot (contém a chave): sem ela, um F5 grava um consentimento novo — que é append-only.
  const consentedPixSignature = ref('');
  const customerProfile = ref<CustomerProfileForm>(emptyCustomerProfileForm());
  const customerProfileStep = ref(0);
  // O snapshot volta do JSON como objeto plano: sem reidratar, métodos como
  // `SimulationTermOptionResource.insuranceAvailableFor` somem depois de um F5.
  const simulation = ref<SimulationResource | null>(
    usableSnapshot?.simulation ? new SimulationResource(usableSnapshot.simulation) : null
  );
  const simulationOptions = ref<SimulationOptionsResource | null>(
    usableSnapshot?.simulationOptions ? new SimulationOptionsResource(usableSnapshot.simulationOptions) : null
  );
  const selectedTermMonths = ref(usableSnapshot?.selectedTermMonths ?? DEFAULT_TERM_MONTHS);
  const selectedAmount = ref<number | null>(usableSnapshot?.selectedAmount ?? null);
  // Qual caixa está marcada (mínimo/metade/máximo), para sobreviver à troca de prazo e à volta do
  // passo de recebimento: null quando a pessoa digitou um valor no slider, sem caixa nenhuma.
  const selectedBand = ref<Faixa | null>(usableSnapshot?.selectedBand ?? null);
  const maximumOfferedByTerm = ref<Record<number, number>>(usableSnapshot?.maximumOfferedByTerm ?? {});
  // Seguro por padrão. O `?? true` também cobre snapshot gravado antes deste campo existir.
  const withInsurance = ref(usableSnapshot?.withInsurance ?? true);
  const proposalId = ref<string | null>(usableSnapshot?.proposalId ?? null);
  // CPF do token, para a chave PIX. Não entra no snapshot: é sempre relido do backend.
  const consumerCpf = ref('');

  const releasedAmount = computed(() => simulation.value?.releasedAmount);
  const termMonths = computed(() => selectedTermMonths.value);
  const selectedTermOption = computed<SimulationTermOptionResource | undefined>(() =>
    simulationOptions.value?.terms.find(option => option.termMonths === selectedTermMonths.value)
  );
  /**
   * Teto de um prazo qualquer, não só do atual: a troca de prazo (e o diálogo de conflito) precisa
   * saber o teto do prazo de destino antes de mudar para ele. Sem oferta ainda vista naquele prazo,
   * o teto do catálogo de opções serve de teto provisório: sem isso o slider some até a simulação
   * voltar.
   */
  function maximumOfferedFor(term: number): number | undefined {
    return (
      maximumOfferedByTerm.value[term] ?? simulationOptions.value?.terms.find(o => o.termMonths === term)?.maximumAmount
    );
  }

  const maximumOfferedAmount = computed(() => maximumOfferedFor(selectedTermMonths.value));

  /** A simulação em tela vale para o prazo, o valor e a ponta de seguro escolhidos agora. */
  const simulationIsCurrent = computed(() => {
    if (simulation.value?.termMonths !== selectedTermMonths.value) {
      return false;
    }
    // Seguro entra aqui, e não só na tela: se a simulação sem seguro for recusada, a proposta ainda
    // apontaria para a simulação com seguro. Enviar isso seria contratar um seguro que a pessoa acabou
    // de recusar — então a oferta em tela deixa de ser válida até a nova simulação voltar.
    if (simulation.value.insuranceIncluded !== withInsurance.value) {
      return false;
    }
    return selectedAmount.value !== null && Math.abs(selectedAmount.value - simulation.value.releasedAmount) < 0.01;
  });

  /** Chave PIX como ela vai para a API: a de CPF é sempre a do token, não a do formulário. */
  const resolvedPixKey = computed(() =>
    customerProfile.value.pixKeyType === 'NaturalRegistrationNumber'
      ? cleanDoc(consumerCpf.value)
      : pixKeyDigitsForApi(customerProfile.value.pixKeyType, customerProfile.value.pixKey)
  );

  const pixSignature = computed(() => `${customerProfile.value.pixKeyType}|${resolvedPixKey.value}`);

  /** True quando a conta de destino em tela ainda não passou por um consentimento. */
  const pixNeedsConsent = computed(() => consentedPixSignature.value !== pixSignature.value);

  /**
   * Registra que a chave PIX em tela foi coberta por um consentimento, e atualiza `consentId` para o
   * registro novo. Consentimento é append-only: a troca de chave grava um segundo registro, e os
   * eventos seguintes precisam propagar esse id novo, não o anterior.
   */
  function markPixConsented(newConsentId: string | null): void {
    consentedPixSignature.value = pixSignature.value;
    consentId.value = newConsentId;
  }

  function resetQualificacao(): void {
    qualificacaoVinculo.value = null;
    qualificacaoIntencao.value = null;
  }

  /** Tudo o que vem depois das duas perguntas: quem acabou de se qualificar recomeça o formulário sem refazê-las. */
  function resetPosQualificacao(): void {
    consentGiven.value = false;
    lgpdAccepted.value = false;
    payrollAcknowledged.value = false;
    consentId.value = null;
    consentedPixSignature.value = '';
    consumerCpf.value = '';
    customerProfile.value = emptyCustomerProfileForm();
    customerProfileStep.value = 0;
    simulation.value = null;
    simulationOptions.value = null;
    selectedTermMonths.value = DEFAULT_TERM_MONTHS;
    selectedAmount.value = null;
    selectedBand.value = null;
    maximumOfferedByTerm.value = {};
    withInsurance.value = true;
    proposalId.value = null;
    clearJourneySnapshot();
  }

  function reset(): void {
    resetQualificacao();
    resetPosQualificacao();
  }

  /**
   * Preenche o formulário com o que o token já sabe do consumidor. Só encosta em campo vazio:
   * o que ele digitou (ou o snapshot da aba restaurou) tem prioridade.
   */
  function applyPrefill(prefill: CustomerProfilePrefillResource): void {
    consumerCpf.value = prefill.cpf;
    const form = customerProfile.value;

    form.name ||= prefill.name?.trim() ?? '';
    form.birthDate ||= isoToBirthDate(prefill.birthDate);
    form.email ||= prefill.email?.trim() ?? '';
    form.phone ||= maskCelular(prefill.phone);

    // Chave PIX tipo CPF = identidade do token. Sempre sobrescreve: snapshot/digitação
    // antiga não pode divergir do consentimento. formatCpf tem fallback perigoso para lixo.
    if (form.pixKeyType === 'NaturalRegistrationNumber') {
      const digits = cleanDoc(prefill.cpf);
      form.pixKey = digits.length === 11 ? formatCpf(digits) : '';
    }
  }

  function initializeSimulationOptions(options: SimulationOptionsResource): void {
    simulationOptions.value = options;
    const recommended = options.terms.some(option => option.termMonths === options.recommendedTermMonths)
      ? options.recommendedTermMonths
      : options.terms.reduce(
          (closest, option) =>
            Math.abs(option.termMonths - DEFAULT_TERM_MONTHS) < Math.abs(closest - DEFAULT_TERM_MONTHS)
              ? option.termMonths
              : closest,
          options.terms[0]?.termMonths ?? DEFAULT_TERM_MONTHS
        );
    selectedTermMonths.value = recommended;
    selectedAmount.value = null;
  }

  function selectTerm(term: number): void {
    // Com uma faixa marcada, o valor dela para o novo prazo é responsabilidade de quem chama (a
    // view recalcula via `valorDaFaixa` e dispara a simulação): aqui só o prazo muda, a faixa
    // continua marcada.
    if (selectedBand.value !== null) {
      selectedTermMonths.value = term;
      return;
    }
    // Valor digitado: trocar o prazo não pode aumentar o valor pedido em silêncio. Quem chama já
    // garantiu que o valor cabe no novo teto (senão mostra o diálogo de conflito e não chega aqui);
    // o clamp aqui é só a rede de segurança para nunca subir em silêncio.
    const newMax = maximumOfferedFor(term);
    selectedAmount.value =
      selectedAmount.value !== null && newMax !== undefined ? Math.min(selectedAmount.value, newMax) : null;
    selectedTermMonths.value = term;
  }

  /**
   * Troca a ponta de seguro sem ir à rede: o backend já devolveu as duas simuladas, cada uma com seu
   * `simulationId`, e é esse id que vai para `POST /v1/proposals`. Trocar é inverter qual delas está
   * selecionada — a que sai vira a alternativa.
   */
  function selecionarSeguro(quer: boolean): void {
    const atual = simulation.value;
    const outra = atual?.insuranceAlternative;
    if (!atual || !outra || atual.insuranceIncluded === quer) {
      return;
    }

    // `outra` cobre exatamente os campos da ponta; prazo, margem e `maximumByTerm` seguem sendo de `atual`.
    simulation.value = new SimulationResource({
      ...atual,
      ...outra,
      insuranceAlternative: new SimulationAlternativeResource(atual),
    });
    withInsurance.value = outra.insuranceIncluded;
    // O valor em tela acompanha a ponta escolhida, senão `simulationIsCurrent` fica falso e o
    // "Continuar" trava sobre uma oferta que está correta.
    selectedAmount.value = outra.releasedAmount;
    // A ciência do desconto vale para a parcela que estava na tela, como em `applySimulation`.
    if (outra.installment !== atual.installment) {
      payrollAcknowledged.value = false;
    }
  }

  function applySimulation(result: SimulationResource, isMaximumSuggestion: boolean): void {
    // A ciência do desconto vale para a parcela que estava na tela: oferta nova, ciência nova.
    if (result.installment !== simulation.value?.installment) {
      payrollAcknowledged.value = false;
    }
    simulation.value = result;
    selectedTermMonths.value = result.termMonths;
    withInsurance.value = result.insuranceIncluded;
    // Entram sempre, e não só na sugestão de máximo: não dependem do valor pedido, e é com eles que a
    // troca de prazo sabe se o valor cabe antes de simular. Sem isso os outros prazos caem no limite do
    // catálogo, que é do produto e não da margem — encurtar o prazo mantendo o valor terminava em recusa.
    for (const [term, ceiling] of Object.entries(result.maximumByTerm ?? {})) {
      maximumOfferedByTerm.value[Number(term)] = ceiling;
    }
    if (isMaximumSuggestion) {
      // Teto do prazo é o maior das duas pontas — sem seguro a mesma margem libera mais. O backend já
      // devolve isso em `maximumByTerm` (ele funde as duas pontas), então este cálculo é só a rede de
      // segurança para resposta sem o campo: `??=` para nunca sobrescrever, e para menos, o que veio de lá.
      maximumOfferedByTerm.value[result.termMonths] ??= Math.max(
        result.releasedAmount,
        result.insuranceAlternative?.releasedAmount ?? 0
      );
      // Pedir sem valor É escolher "o máximo": explícito aqui, em vez de deduzir da igualdade entre
      // o valor em tela e o teto (que quebraria se os dois só coincidissem por acaso).
      selectedBand.value = 'maximo';
    }
    selectedAmount.value = result.releasedAmount;
  }

  function buildCustomerProfilePayload(): CustomerProfileInput {
    const form = customerProfile.value;
    if (!form.civilStatus) {
      throw new Error('Estado civil é obrigatório');
    }
    const occupation = form.occupation.trim();
    if (!occupation) {
      throw new Error('Profissão é obrigatória');
    }
    const birthDate = birthDateToIso(form.birthDate);
    if (!birthDate) {
      throw new Error('Data de nascimento é obrigatória');
    }
    // Sem o CPF do token não há chave PIX de CPF: erro claro aqui em vez de 422 da API.
    if (!resolvedPixKey.value) {
      throw new Error('Não foi possível identificar sua chave PIX');
    }

    return {
      name: form.name.trim(),
      birthDate,
      phone: cleanDoc(form.phone),
      email: form.email.trim(),
      civilStatus: form.civilStatus,
      nationality: form.nationality.trim(),
      occupation,
      address: {
        addressName: form.addressName.trim(),
        zipCode: form.zipCode,
        district: form.district.trim(),
        city: form.city.trim(),
        uf: form.uf.trim().toUpperCase(),
        number: form.number.trim() || undefined,
        complement: form.complement.trim() || undefined,
      },
      paymentAccount: {
        pixKeyType: form.pixKeyType,
        pixKey: resolvedPixKey.value,
      },
    };
  }

  const snapshotState = computed<JourneySnapshot>(() => ({
    consentGiven: consentGiven.value,
    lgpdAccepted: lgpdAccepted.value,
    payrollAcknowledged: payrollAcknowledged.value,
    consentId: consentId.value,
    simulation: simulation.value,
    simulationOptions: simulationOptions.value,
    selectedTermMonths: selectedTermMonths.value,
    selectedAmount: selectedAmount.value,
    selectedBand: selectedBand.value,
    maximumOfferedByTerm: maximumOfferedByTerm.value,
    withInsurance: withInsurance.value,
    proposalId: proposalId.value,
    userHash: currentUserHash(),
  }));

  // Sem `deep`: `snapshotState` é um computed que devolve um objeto novo a cada recálculo, então a
  // troca de referência já dispara o watcher — `deep` só somaria travessia recursiva de graça.
  //
  // flush sync: a gravação precisa acontecer antes da navegação para a assinatura, que descarrega a
  // página sem esperar microtasks. Sem o formulário no snapshot, isso roda por evento da jornada
  // (simulou, aceitou) e não a cada tecla digitada.
  watch(snapshotState, value => saveJourneySnapshot(value), { flush: 'sync' });

  // Troca de sessão invalida a jornada: consentimento, perfil e simulação eram da anterior.
  watch(
    () => sessionService.token?.payload.hash,
    (novo, anterior) => {
      if (novo !== anterior) {
        reset();
      }
    },
    { flush: 'sync' }
  );

  return {
    qualificacaoVinculo,
    qualificacaoIntencao,
    consentGiven,
    lgpdAccepted,
    payrollAcknowledged,
    consentId,
    pixNeedsConsent,
    markPixConsented,
    consumerCpf,
    customerProfile,
    customerProfileStep,
    simulation,
    simulationOptions,
    selectedTermMonths,
    selectedAmount,
    selectedBand,
    selectedTermOption,
    maximumOfferedAmount,
    maximumOfferedFor,
    withInsurance,
    selecionarSeguro,
    simulationIsCurrent,
    proposalId,
    releasedAmount,
    termMonths,
    reset,
    resetQualificacao,
    resetPosQualificacao,
    applyPrefill,
    initializeSimulationOptions,
    selectTerm,
    applySimulation,
    buildCustomerProfilePayload,
  };
});
